/**
 * Family & settings.
 *
 * Three things the app has to know, and nothing else: when the HSA was
 * established, who is in the family, and the two yearly answers that stand in
 * for a checkbox on every receipt (FR-14 as revised).
 *
 * The behaviour knobs that used to live here — invoice threshold, flag
 * sensitivity, expected withdrawals, retention — each had one sensible value
 * for a household, so they are decisions the app makes. Their values remain in
 * the config record.
 *
 * Every write here is admin-only server-side (TR-39). A member sees the values
 * and cannot change them; the fields are shown disabled rather than hidden so
 * the permission model is visible.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, uploadEstablishmentEvidence } from '../api.js';
import { claims, accountSettingsUrl } from '../auth.js';
import { useApi, useAction } from '../hooks.js';
import { ErrorBox, Notice, PageHead, Pill, Skeleton, formatDate, toast } from '../ui.js';
import type { AppConfigResponse, CoveredPerson, DashboardResponse, TaxYearDeclaration } from '../types.js';

const RELATIONSHIPS = [
  { id: 'self', label: 'Me (the HSA owner)' },
  { id: 'spouse', label: 'Spouse' },
  { id: 'dependent', label: 'Dependent' },
];

export function Settings() {
  const config = useApi<AppConfigResponse>('/api/config');
  const persons = useApi<{ persons: CoveredPerson[] }>('/api/persons');
  const dash = useApi<DashboardResponse>('/api/dashboard');
  const user = claims();
  const isAdmin = (user?.['cognito:groups'] ?? []).includes('admin');
  const location = useLocation();

  // Home links straight to a section (#hsa, #family, #yearly).
  const ready = Boolean(config.data && persons.data);
  useEffect(() => {
    if (!ready || !location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [ready, location.hash]);

  if (config.error) return <ErrorBox error={config.error} />;
  if (!config.data || !persons.data) return <Skeleton rows={4} />;

  const cfg = config.data.config;
  const people = persons.data.persons;

  return (
    <>
      <PageHead title="Family & settings">
        Signed in as {user?.email ?? user?.['cognito:username']}
        {isAdmin ? '' : '. Only the account owner can change these.'}
      </PageHead>

      <HsaAccount cfg={cfg} isAdmin={isAdmin} onChange={config.reload} />
      <Family
        people={people}
        defaultId={cfg.default_patient_id}
        isAdmin={isAdmin}
        onChange={() => {
          persons.reload();
          config.reload();
        }}
      />
      <Yearly
        declarations={cfg.tax_year_declarations ?? {}}
        due={dash.data?.declarations?.due ?? []}
        isAdmin={isAdmin}
        onChange={() => {
          config.reload();
          dash.reload();
        }}
      />

      <h2 className="section">
        <span>Sign-in</span>
      </h2>
      <div className="card stack tight">
        <p className="small muted">
          Passwords, passkeys and the authenticator app are managed by the sign-in service. A passkey
          signs you in with a fingerprint or face in one step.
        </p>
        <div className="actions">
          <a className="btn" href={accountSettingsUrl()} target="_blank" rel="noreferrer">
            Manage passkeys and sign-in
          </a>
        </div>
      </div>

      <details className="more">
        <summary>Mileage rates</summary>
        <MileageRates rates={cfg.mileage_rate_by_year} isAdmin={isAdmin} onChange={config.reload} />
      </details>
    </>
  );
}

// ---------------------------------------------------------------------------

function HsaAccount({
  cfg,
  isAdmin,
  onChange,
}: {
  cfg: AppConfigResponse['config'];
  isAdmin: boolean;
  onChange: () => void;
}) {
  const [date, setDate] = useState(cfg.hsa_establishment_date ?? '');
  const [custodian, setCustodian] = useState(cfg.custodian);
  const [account, setAccount] = useState(cfg.hsa_account_identifier ?? '');
  useEffect(() => {
    setDate(cfg.hsa_establishment_date ?? '');
    setCustodian(cfg.custodian);
    setAccount(cfg.hsa_account_identifier ?? '');
  }, [cfg.hsa_establishment_date, cfg.custodian, cfg.hsa_account_identifier]);

  const save = useAction(async () => {
    await api.put('/api/config', {
      hsa_establishment_date: date || null,
      custodian,
      hsa_account_identifier: account || null,
    });
    toast('Saved');
    onChange();
  });

  // Opened through a freshly minted five-minute address rather than a stored
  // link — the archive is private and its addresses are never long-lived (§9).
  const view = useAction(async () => {
    const { url } = await api.get<{ url: string }>('/api/config/establishment-evidence');
    window.open(url, '_blank', 'noopener');
  });
  const upload = useAction(async (file: File) => {
    await uploadEstablishmentEvidence(file);
    toast('Attached');
    onChange();
  });

  const changed =
    date !== (cfg.hsa_establishment_date ?? '') ||
    custodian !== cfg.custodian ||
    account !== (cfg.hsa_account_identifier ?? '');
  const evidence = cfg.establishment_date_evidence_s3_key;

  return (
    <>
      <h2 className="section" id="hsa">
        <span>The HSA</span>
      </h2>
      <div className="card stack">
        <div className="field">
          <label htmlFor="estdate">Date the HSA was established</label>
          <input
            id="estdate"
            type="date"
            disabled={!isAdmin}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ maxWidth: 220 }}
          />
          <span className="hint">
            Usually the day it was first funded. If it was opened by rollover from an earlier HSA, or
            within 18 months of one that still held money, use the earlier account’s date. Expenses from
            before this date cannot be claimed.
          </span>
        </div>

        <div className="field">
          <span className="label">Proof of that date</span>
          {evidence ? (
            <div className="row-wrap">
              <Pill tone="ok">Attached</Pill>
              <span className="small muted">{evidence.split('/').pop()}</span>
              <button className="quiet" onClick={() => void view.run()} disabled={view.pending}>
                View
              </button>
            </div>
          ) : (
            <span className="hint">
              Any one of: the first statement showing a balance, the account-opening confirmation, or the
              first year’s Form 5498-SA. It goes into every audit packet.
            </span>
          )}
          {isAdmin ? (
            <input
              type="file"
              aria-label={evidence ? 'Replace the proof document' : 'Attach the proof document'}
              accept="application/pdf,image/*"
              disabled={upload.pending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void upload.run(file);
                e.target.value = '';
              }}
            />
          ) : null}
          {upload.error ? <ErrorBox error={upload.error} /> : null}
          {view.error ? <ErrorBox error={view.error} /> : null}
        </div>

        <div className="fields two">
          <div className="field">
            <label htmlFor="custodian">Where the HSA is held</label>
            <input id="custodian" disabled={!isAdmin} value={custodian} onChange={(e) => setCustodian(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="acct">Account number, last 4</label>
            <input id="acct" disabled={!isAdmin} value={account} onChange={(e) => setAccount(e.target.value)} />
            <span className="hint">Used to check that an imported file is for this account.</span>
          </div>
        </div>

        {save.error ? <ErrorBox error={save.error} /> : null}
        {isAdmin ? (
          <div className="actions">
            <button className="primary" onClick={() => void save.run()} disabled={save.pending || !changed || !custodian.trim()}>
              Save
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}

function Family({
  people,
  defaultId,
  isAdmin,
  onChange,
}: {
  people: CoveredPerson[];
  defaultId: string | null;
  isAdmin: boolean;
  onChange: () => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('self');

  const add = useAction(async () => {
    await api.post('/api/persons', { display_name: name.trim(), relationship });
    setName('');
    toast('Added');
    onChange();
  });
  const setDefault = useAction(async (id: string) => {
    await api.put('/api/config', { default_patient_id: id || null });
    toast('Saved');
    onChange();
  });

  const active = people.filter((p) => p.active);

  return (
    <>
      <h2 className="section" id="family">
        <span>Family</span>
      </h2>
      <div className="card stack">
        <p className="small muted">
          You, your spouse, and anyone who was your tax dependent when the care happened. They do not
          need to be on your health plan.
        </p>

        {people.length > 0 ? (
          <div className="list">
            {people.map((p) =>
              editing === p.person_id ? (
                <EditPerson
                  key={p.person_id}
                  person={p}
                  onDone={() => {
                    setEditing(null);
                    onChange();
                  }}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div className="item plain" key={p.person_id}>
                  <span>
                    <div className="title" style={p.active ? undefined : { opacity: 0.55 }}>
                      {p.display_name}
                    </div>
                    <div className="sub">
                      {RELATIONSHIPS.find((r) => r.id === p.relationship)?.label ?? p.relationship}
                      {p.active ? '' : ' · no longer offered for new expenses'}
                      {p.person_id === defaultId ? ' · default' : ''}
                    </div>
                  </span>
                  {isAdmin ? (
                    <button className="quiet" onClick={() => setEditing(p.person_id)}>
                      Edit
                    </button>
                  ) : null}
                </div>
              ),
            )}
          </div>
        ) : null}

        {isAdmin ? (
          <div className="fields two">
            <div className="field">
              <label htmlFor="pname">Add a person</label>
              <input id="pname" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="prel">Relationship</label>
              <div className="actions">
                <select id="prel" style={{ flex: 1 }} value={relationship} onChange={(e) => setRelationship(e.target.value)}>
                  {RELATIONSHIPS.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <button onClick={() => void add.run()} disabled={add.pending || name.trim() === ''}>
                  Add
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {add.error ? <ErrorBox error={add.error} /> : null}

        {active.length > 0 ? (
          <div className="field">
            <label htmlFor="default">Who most receipts are for</label>
            <select
              id="default"
              disabled={!isAdmin || setDefault.pending}
              value={defaultId ?? ''}
              onChange={(e) => void setDefault.run(e.target.value)}
              style={{ maxWidth: 320 }}
            >
              <option value="">Ask every time</option>
              {active.map((p) => (
                <option key={p.person_id} value={p.person_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
            <span className="hint">Filled in on new receipts and Amazon items. You can change it on each one.</span>
            {setDefault.error ? <ErrorBox error={setDefault.error} /> : null}
          </div>
        ) : null}
      </div>
    </>
  );
}

function EditPerson({
  person,
  onDone,
  onCancel,
}: {
  person: CoveredPerson;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(person.display_name);
  const [relationship, setRelationship] = useState(person.relationship);
  const [active, setActive] = useState(person.active);
  const save = useAction(async () => {
    await api.patch(`/api/persons/${person.person_id}`, { display_name: name.trim(), relationship, active });
    toast('Saved');
    onDone();
  });
  return (
    <div className="stack tight" style={{ padding: 14, borderBottom: '1px solid var(--line)' }}>
      <div className="fields two">
        <div className="field">
          <label htmlFor={`n-${person.person_id}`}>Name</label>
          <input id={`n-${person.person_id}`} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`r-${person.person_id}`}>Relationship</label>
          <select id={`r-${person.person_id}`} value={relationship} onChange={(e) => setRelationship(e.target.value)}>
            {RELATIONSHIPS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="check-row">
        <input id={`a-${person.person_id}`} type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <label htmlFor={`a-${person.person_id}`}>
          Offer for new expenses <span className="muted">(their past expenses are kept either way)</span>
        </label>
      </div>
      {save.error ? <ErrorBox error={save.error} /> : null}
      <div className="actions end">
        <button onClick={onCancel}>Cancel</button>
        <button className="primary" onClick={() => void save.run()} disabled={save.pending || name.trim() === ''}>
          Save
        </button>
      </div>
    </div>
  );
}

/**
 * FR-14 as revised — the yearly declaration.
 *
 * Pub 969 asks for records showing an expense was not paid by another source
 * and not itemized. Both are facts about the household and the tax year, so
 * they are asked once a year here rather than ticked on every receipt. A yes
 * to either brings the per-receipt question back for that year only.
 */
function Yearly({
  declarations,
  due,
  isAdmin,
  onChange,
}: {
  declarations: Record<string, TaxYearDeclaration>;
  due: string[];
  isAdmin: boolean;
  onChange: () => void;
}) {
  const lastYear = String(new Date().getFullYear() - 1);
  const years = [...new Set([...due, ...Object.keys(declarations), lastYear])].sort().reverse();

  return (
    <>
      <h2 className="section" id="yearly">
        <span>Two questions a year</span>
      </h2>
      <div className="card stack">
        <p className="small muted">
          These answers go on the audit packet in place of a checkbox on every receipt. Answer for a year
          once its tax return is filed.
        </p>
        {years.map((y) => (
          <YearRow key={y} year={y} existing={declarations[y] ?? null} isDue={due.includes(y)} isAdmin={isAdmin} onChange={onChange} />
        ))}
      </div>
    </>
  );
}

function YearRow({
  year,
  existing,
  isDue,
  isAdmin,
  onChange,
}: {
  year: string;
  existing: TaxYearDeclaration | null;
  isDue: boolean;
  isAdmin: boolean;
  onChange: () => void;
}) {
  const [open, setOpen] = useState(isDue);
  const [otherPlan, setOtherPlan] = useState<boolean | null>(existing?.other_plan_could_pay ?? null);
  const [itemized, setItemized] = useState<boolean | null>(existing?.itemized_medical ?? null);

  const save = useAction(async () => {
    await api.put(`/api/config/declarations/${year}`, {
      other_plan_could_pay: otherPlan,
      itemized_medical: itemized,
    });
    toast(`${year} saved`);
    setOpen(false);
    onChange();
  });

  const eitherYes = existing ? existing.other_plan_could_pay || existing.itemized_medical : false;

  if (!open) {
    return (
      <div className="row-wrap" style={{ justifyContent: 'space-between' }}>
        <span>
          <strong>{year}</strong>{' '}
          {existing ? (
            <Pill tone={eitherYes ? 'warn' : 'ok'}>{eitherYes ? 'Yes to one: asked per receipt' : 'No to both'}</Pill>
          ) : (
            <Pill>Not answered</Pill>
          )}
          {existing ? (
            <span className="xs muted"> · {formatDate(existing.answered_at, { year: 'always' })}</span>
          ) : null}
        </span>
        {isAdmin ? (
          <button className="quiet" onClick={() => setOpen(true)}>
            {existing ? 'Change' : 'Answer'}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="stack tight" style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      <strong>{year}</strong>
      <YesNo
        id={`plan-${year}`}
        question={`In ${year}, could an FSA, HRA, or another plan have paid any of the family’s medical expenses?`}
        value={otherPlan}
        onChange={setOtherPlan}
        disabled={!isAdmin}
      />
      <YesNo
        id={`item-${year}`}
        question={`Did you itemize medical expenses on Schedule A for ${year}?`}
        value={itemized}
        onChange={setItemized}
        disabled={!isAdmin}
      />
      {otherPlan || itemized ? (
        <Notice kind="warn" title={`Receipts from ${year} will each ask for confirmation`}>
          Because the answer can differ from one expense to the next, each {year} expense will ask you to
          confirm it was not paid or deducted elsewhere before it is filed.
        </Notice>
      ) : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
      <div className="actions end">
        <button onClick={() => setOpen(false)}>Cancel</button>
        <button
          className="primary"
          onClick={() => void save.run()}
          disabled={!isAdmin || save.pending || otherPlan === null || itemized === null}
        >
          Save {year}
        </button>
      </div>
    </div>
  );
}

function YesNo({
  id,
  question,
  value,
  onChange,
  disabled,
}: {
  id: string;
  question: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="field">
      <span className="label" id={id}>
        {question}
      </span>
      <div className="chips" role="group" aria-labelledby={id}>
        <button className="chip" aria-pressed={value === false} disabled={disabled} onClick={() => onChange(false)}>
          No
        </button>
        <button className="chip" aria-pressed={value === true} disabled={disabled} onClick={() => onChange(true)}>
          Yes
        </button>
      </div>
    </div>
  );
}

function MileageRates({
  rates,
  isAdmin,
  onChange,
}: {
  rates: Record<string, number>;
  isAdmin: boolean;
  onChange: () => void;
}) {
  const [year, setYear] = useState(String(new Date().getFullYear() + 1));
  const [rate, setRate] = useState('');
  const save = useAction(async () => {
    await api.put('/api/config', { mileage_rate_by_year: { ...rates, [year]: Number(rate) } });
    setRate('');
    toast(`${year} rate saved`);
    onChange();
  });
  return (
    <div className="stack tight">
      <p className="xs muted">
        The IRS medical mileage rate, in cents per mile, by year of travel. The mileage form asks for a
        year’s rate when it is missing.
      </p>
      <div className="list">
        {Object.entries(rates)
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([y, cents]) => (
            <div className="item plain" key={y} style={{ minHeight: 44 }}>
              <span>{y}</span>
              <span className="end">{cents}¢</span>
            </div>
          ))}
      </div>
      {isAdmin ? (
        <div className="actions">
          <input aria-label="Year" style={{ width: 96 }} value={year} onChange={(e) => setYear(e.target.value)} />
          <input
            aria-label="Cents per mile"
            placeholder="¢ per mile"
            inputMode="decimal"
            style={{ width: 130 }}
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
          <button onClick={() => void save.run()} disabled={save.pending || !/^\d{4}$/.test(year) || !(Number(rate) > 0)}>
            Save
          </button>
        </div>
      ) : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
    </div>
  );
}
